<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';

const route = useRoute();
const slugOrId = computed(() => route.params.slug_or_id || route.params.params);

const isLoading = ref(true);
const isBooking = ref(false);
const error = ref('');
const successData = ref(null);

const company = ref({});
const services = ref([]);
const professionals = ref([]);

// View Mode: 'agenda' (visão diária) | 'grid' (visão grade semanal)
const viewMode = ref('grid');

// Booking Flow State
const selectedService = ref(null);
const selectedProfessional = ref(null);
const activePeriod = ref('morning'); // 'morning' | 'afternoon' | 'night' | 'all'

// Agenda View Date
const selectedDate = ref(new Date().toISOString().split('T')[0]);

// Weekly Navigation State (Agenda Mode)
const currentWeekStart = ref(getMondayOfDate(new Date()));
const weeklySlotsMap = ref({});
const isLoadingWeeklySlots = ref(false);

// Fixed Grid State (Grade de Vagas Fixa Perpétua)
const fixedGridMatrix = ref({});
const isLoadingFixedGrid = ref(false);

const selectedSlotInfo = ref(null); // { mode, dateStr, dayKey, timeSlot, slot, slotData }
const selectedSlotsList = ref([]); // Multi-seleção na Grade Fixa para 2x ou 3x por semana
const showBookingModal = ref(false);
const isTrialClass = ref(false);
const bookingBillingType = ref('recurring'); // 'recurring' | 'package' | 'single'
const hasCopiedWhatsApp = ref(false);

const patientForm = ref({
  name: '',
  phone: '',
  email: '',
  notes: '',
});

// Cópia Inteligente das Vagas Formatadas para o WhatsApp (Foco na Atendente)
const copySlotsForWhatsApp = async () => {
  const serviceName = selectedService.value?.name || 'Atendimento';
  const periodLabel = {
    morning: 'Manhã',
    afternoon: 'Tarde',
    night: 'Noite',
    all: 'Dia Todo',
  }[activePeriod.value] || 'Geral';

  let lines = [];
  lines.push(`Olá! Temos as seguintes vagas disponíveis para *${serviceName}* no período da *${periodLabel}*:\n`);

  if (viewMode.value === 'grid') {
    // Modo Grade Fixa (Dias da Semana)
    fixedDaysOfWeek.forEach(day => {
      const availableTimes = [];
      displayedHours.value.forEach(hour => {
        const slot = fixedGridMatrix.value[day.key]?.[hour];
        if (slot && slot.is_available) {
          const spotsText = slot.free_spots <= 2 ? `(${slot.free_spots} vaga${slot.free_spots > 1 ? 's' : ''} restante${slot.free_spots > 1 ? 's' : ''})` : `(${slot.free_spots} vagas)`;
          availableTimes.push(`${hour} ${spotsText}`);
        }
      });
      if (availableTimes.length > 0) {
        lines.push(`📅 *${day.full}:* ${availableTimes.join(', ')}`);
      }
    });
  } else {
    // Modo Agenda por Datas
    agendaWeekDaysList.value.forEach(day => {
      const daySlots = weeklySlotsMap.value[day.dateStr] || [];
      const availableTimes = [];
      displayedHours.value.forEach(hour => {
        const slot = daySlots.find(s => s.time_label === hour);
        if (slot) {
          const spotsText = slot.total_spots <= 2 ? `(${slot.total_spots} vaga${slot.total_spots > 1 ? 's' : ''} restante${slot.total_spots > 1 ? 's' : ''})` : `(${slot.total_spots} vagas)`;
          availableTimes.push(`${hour} ${spotsText}`);
        }
      });
      if (availableTimes.length > 0) {
        lines.push(`📅 *${day.fullLabel} (${day.formattedDay}):* ${availableTimes.join(', ')}`);
      }
    });
  }

  if (lines.length === 1) {
    lines.push(`_Nenhum horário livre encontrado no período selecionado._`);
  } else {
    lines.push(`\nQual dessas opções fica melhor para o seu atendimento?`);
  }

  const messageText = lines.join('\n');
  try {
    await navigator.clipboard.writeText(messageText);
    hasCopiedWhatsApp.value = true;
    setTimeout(() => {
      hasCopiedWhatsApp.value = false;
    }, 3500);
  } catch (err) {
    console.error('Erro ao copiar para clipboard:', err);
    prompt('Copie o texto abaixo para enviar no WhatsApp:', messageText);
  }
};

function getMondayOfDate(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

const fixedDaysOfWeek = [
  { key: 'monday', label: 'SEG', full: 'Segunda-feira' },
  { key: 'tuesday', label: 'TER', full: 'Terça-feira' },
  { key: 'wednesday', label: 'QUA', full: 'Quarta-feira' },
  { key: 'thursday', label: 'QUI', full: 'Quinta-feira' },
  { key: 'friday', label: 'SEX', full: 'Sexta-feira' },
  { key: 'saturday', label: 'SÁB', full: 'Sábado' },
];

const todayDayKey = computed(() => {
  const dayIndex = new Date().getDay();
  const map = {
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
  };
  return map[dayIndex] || null;
});

const agendaWeekDaysList = computed(() => {
  const list = [];
  const labels = ['SEG.', 'TER.', 'QUA.', 'QUI.', 'SEX.', 'SÁB.', 'DOM.'];
  const fullLabels = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

  for (let i = 0; i < 7; i++) {
    const d = new Date(currentWeekStart.value);
    d.setDate(d.getDate() + i);
    const dateStr = d.toISOString().split('T')[0];
    const isToday = dateStr === new Date().toISOString().split('T')[0];

    list.push({
      index: i,
      label: labels[i],
      fullLabel: fullLabels[i],
      dateObj: d,
      dateStr: dateStr,
      formattedDay: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`,
      isToday,
    });
  }
  return list;
});

const agendaWeekRangeLabel = computed(() => {
  if (agendaWeekDaysList.value.length === 0) return '';
  const first = agendaWeekDaysList.value[0].dateObj;
  const last = agendaWeekDaysList.value[agendaWeekDaysList.value.length - 1].dateObj;

  const fDay = String(first.getDate()).padStart(2, '0');
  const fMonth = String(first.getMonth() + 1).padStart(2, '0');
  const lDay = String(last.getDate()).padStart(2, '0');
  const lMonth = String(last.getMonth() + 1).padStart(2, '0');

  return `${fDay}/${fMonth} a ${lDay}/${lMonth}`;
});

const morningHours = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00'];
const afternoonHours = ['12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];
const nightHours = ['18:00', '19:00', '20:00', '21:00', '22:00'];

const displayedHours = computed(() => {
  if (activePeriod.value === 'morning') return morningHours;
  if (activePeriod.value === 'afternoon') return afternoonHours;
  if (activePeriod.value === 'night') return nightHours;
  return [...morningHours, ...afternoonHours, ...nightHours];
});

const fetchPublicConfig = async () => {
  isLoading.value = true;
  error.value = '';
  try {
    const slug = slugOrId.value;
    const res = await axios.get(`/api/v1/scheduling/public/${slug}`);
    company.value = res.data.company || {};
    services.value = res.data.services || [];
    professionals.value = res.data.professionals || [];

    if (services.value.length > 0) {
      selectedService.value = services.value[0];
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Página de agendamento não encontrada.';
  } finally {
    isLoading.value = false;
  }
};

const fetchWeeklySlots = async () => {
  if (!slugOrId.value || agendaWeekDaysList.value.length === 0) return;

  isLoadingWeeklySlots.value = true;
  try {
    const startDateStr = agendaWeekDaysList.value[0].dateStr;
    const endDateStr = agendaWeekDaysList.value[agendaWeekDaysList.value.length - 1].dateStr;

    const params = {
      start_date: startDateStr,
      end_date: endDateStr,
    };
    if (selectedService.value) params.service_id = selectedService.value.id;
    if (selectedProfessional.value) params.professional_id = selectedProfessional.value.id;

    const res = await axios.get(`/api/v1/scheduling/public/${slugOrId.value}/available_slots`, { params });
    weeklySlotsMap.value = res.data.weekly_slots || {};
  } catch (err) {
    console.error('Erro ao carregar agenda semanal:', err);
    weeklySlotsMap.value = {};
  } finally {
    isLoadingWeeklySlots.value = false;
  }
};

const fetchFixedGridData = async () => {
  if (!slugOrId.value) return;

  isLoadingFixedGrid.value = true;
  try {
    const params = {};
    if (selectedService.value) params.service_id = selectedService.value.id;
    if (selectedProfessional.value) params.professional_id = selectedProfessional.value.id;

    const res = await axios.get(`/api/v1/scheduling/public/${slugOrId.value}/fixed_grid`, { params });
    fixedGridMatrix.value = res.data.grid || {};
  } catch (err) {
    console.error('Erro ao carregar grade fixa:', err);
    fixedGridMatrix.value = {};
  } finally {
    isLoadingFixedGrid.value = false;
  }
};

const nextWeek = () => {
  const next = new Date(currentWeekStart.value);
  next.setDate(next.getDate() + 7);
  currentWeekStart.value = next;
  selectedSlotInfo.value = null;
  selectedSlotsList.value = [];
  fetchWeeklySlots();
};

const prevWeek = () => {
  const prev = new Date(currentWeekStart.value);
  prev.setDate(prev.getDate() - 7);
  currentWeekStart.value = prev;
  selectedSlotInfo.value = null;
  selectedSlotsList.value = [];
  fetchWeeklySlots();
};

const goToToday = () => {
  currentWeekStart.value = getMondayOfDate(new Date());
  selectedSlotInfo.value = null;
  selectedSlotsList.value = [];
  fetchWeeklySlots();
};

const getSlotForAgendaCell = (dateStr, hourStr) => {
  const daySlots = weeklySlotsMap.value[dateStr] || [];
  return daySlots.find(s => s.time_label === hourStr);
};

const getSlotForFixedGridCell = (dayKey, hourStr) => {
  return fixedGridMatrix.value[dayKey]?.[hourStr] || null;
};

const modalSelectedDays = ref(['monday']);
const bookingTimeSlot = ref('08:00');
const bookingTotalSessions = ref(10);
const activeModalShift = ref('morning');
const bookingStartDate = ref(new Date().toISOString().split('T')[0]);
const bookingDueDay = ref(5);
const bookingPrice = ref(0);

const modalShiftTimes = computed(() => {
  if (activeModalShift.value === 'morning') return morningHours;
  if (activeModalShift.value === 'afternoon') return afternoonHours;
  return nightHours;
});

const isFixedSlotSelected = (dayKey, hourStr) => {
  return selectedSlotsList.value.some(s => s.dayKey === dayKey && s.timeSlot === hourStr);
};

const toggleFixedGridSlot = (dayKey, hourStr, slotData) => {
  if (!slotData || !slotData.is_available) return;
  const dayObj = fixedDaysOfWeek.find(d => d.key === dayKey);
  const existingIdx = selectedSlotsList.value.findIndex(s => s.dayKey === dayKey && s.timeSlot === hourStr);

  if (existingIdx >= 0) {
    selectedSlotsList.value.splice(existingIdx, 1);
  } else {
    selectedSlotsList.value.push({
      mode: 'grid',
      dayKey,
      dayLabel: dayObj?.full || dayKey,
      timeSlot: hourStr,
      slotData,
    });
  }
};

const setBillingType = (type) => {
  bookingBillingType.value = type;
  if (type === 'single' && modalSelectedDays.value.length > 1) {
    modalSelectedDays.value = [modalSelectedDays.value[0]];
  }
};

const toggleModalDay = (dayKey) => {
  if (bookingBillingType.value === 'single' || isTrialClass.value) {
    modalSelectedDays.value = [dayKey];
    return;
  }

  const idx = modalSelectedDays.value.indexOf(dayKey);
  if (idx >= 0) {
    if (modalSelectedDays.value.length > 1) {
      modalSelectedDays.value.splice(idx, 1);
    }
  } else {
    modalSelectedDays.value.push(dayKey);
  }
};

const setupModalTimesAndPrice = (time) => {
  bookingTimeSlot.value = time || '08:00';
  if (afternoonHours.includes(bookingTimeSlot.value)) {
    activeModalShift.value = 'afternoon';
  } else if (nightHours.includes(bookingTimeSlot.value)) {
    activeModalShift.value = 'night';
  } else {
    activeModalShift.value = 'morning';
  }
  bookingPrice.value = selectedService.value?.price || 0;
};

const openBookingModalWithSelection = () => {
  if (selectedSlotsList.value.length === 0) return;
  const first = selectedSlotsList.value[0];
  selectedSlotInfo.value = first;
  modalSelectedDays.value = selectedSlotsList.value.map(s => s.dayKey);
  setupModalTimesAndPrice(first.timeSlot);
  showBookingModal.value = true;
};

const selectAgendaSlot = (dateStr, slot) => {
  if (!slot) return;
  selectedSlotInfo.value = {
    mode: 'agenda',
    dateStr,
    timeSlot: slot.time_label,
    slot,
  };
  bookingStartDate.value = dateStr;
  setupModalTimesAndPrice(slot.time_label);
  showBookingModal.value = true;
};

const selectFixedGridSlot = (dayKey, hourStr, slotData) => {
  if (!slotData || !slotData.is_available) return;
  const dayObj = fixedDaysOfWeek.find(d => d.key === dayKey);
  selectedSlotInfo.value = {
    mode: 'grid',
    dayKey,
    dayLabel: dayObj?.full || dayKey,
    timeSlot: hourStr,
    slotData,
  };
  modalSelectedDays.value = [dayKey];
  setupModalTimesAndPrice(hourStr);
  showBookingModal.value = true;
};

const closeBookingModal = () => {
  showBookingModal.value = false;
};

const confirmBooking = async () => {
  if (!patientForm.value.name.trim()) {
    alert('Por favor, informe seu nome completo.');
    return;
  }
  if (!patientForm.value.phone.trim()) {
    alert('Por favor, informe seu WhatsApp de contato.');
    return;
  }
  if (!selectedSlotInfo.value) {
    alert('Por favor, selecione um horário.');
    return;
  }

  isBooking.value = true;
  try {
    const daysToEnroll = (selectedSlotInfo.value.mode === 'grid' && modalSelectedDays.value.length > 0)
      ? modalSelectedDays.value
      : [selectedSlotInfo.value.dayKey || 'monday'];

    let lastRes = null;

    if (selectedSlotInfo.value.mode === 'grid') {
      if (bookingBillingType.value === 'single' || isTrialClass.value) {
        // Sessão Avulsa ou Aula Experimental: utiliza a Data do Atendimento selecionada diretamente
        const targetDate = bookingStartDate.value || new Date().toISOString().split('T')[0];
        const startTimeStr = `${targetDate}T${bookingTimeSlot.value}:00`;

        const payload = {
          patient_name: patientForm.value.name.trim(),
          patient_phone: patientForm.value.phone.trim(),
          patient_email: patientForm.value.email.trim(),
          service_id: selectedService.value.id,
          professional_id: selectedProfessional.value?.id || null,
          start_time: startTimeStr,
          is_trial_class: isTrialClass.value,
          billing_type: 'single',
          price: bookingPrice.value || 0,
          notes: patientForm.value.notes.trim(),
        };

        lastRes = await axios.post(`/api/v1/scheduling/public/${slugOrId.value}/book`, payload);
      } else {
        // Mensalidade ou Pacote Recorrente: itera sobre cada dia fixo da semana selecionado
        for (const dayKey of daysToEnroll) {
          const dayObj = fixedDaysOfWeek.find(d => d.key === dayKey);
          const daysOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
          const targetDayIndex = daysOrder.indexOf(dayKey);

          const d = new Date(bookingStartDate.value || new Date());
          let diff = (targetDayIndex - d.getDay() + 7) % 7;
          if (diff === 0) diff = 7;
          d.setDate(d.getDate() + diff);
          const nextDateStr = d.toISOString().split('T')[0];
          const startTimeStr = `${nextDateStr}T${bookingTimeSlot.value}:00`;

          const payload = {
            patient_name: patientForm.value.name.trim(),
            patient_phone: patientForm.value.phone.trim(),
            patient_email: patientForm.value.email.trim(),
            service_id: selectedService.value.id,
            professional_id: selectedProfessional.value?.id || null,
            start_time: startTimeStr,
            is_trial_class: false,
            billing_type: bookingBillingType.value,
            due_day: bookingDueDay.value || 5,
            price: bookingPrice.value || 0,
            total_sessions: bookingBillingType.value === 'package' ? bookingTotalSessions.value : 10,
            notes: `[Matrícula / Vaga Fixa semanal em ${dayObj?.full || dayKey} às ${bookingTimeSlot.value} (${daysToEnroll.length}x/semana)] | ${patientForm.value.notes.trim()}`,
          };

          lastRes = await axios.post(`/api/v1/scheduling/public/${slugOrId.value}/book`, payload);
        }
      }
    } else {
      // Modo Agenda
      const startTimeStr = `${selectedSlotInfo.value.dateStr}T${bookingTimeSlot.value}:00`;
      const payload = {
        patient_name: patientForm.value.name.trim(),
        patient_phone: patientForm.value.phone.trim(),
        patient_email: patientForm.value.email.trim(),
        service_id: selectedService.value.id,
        professional_id: selectedProfessional.value?.id || null,
        start_time: startTimeStr,
        is_trial_class: isTrialClass.value,
        billing_type: bookingBillingType.value,
        price: bookingPrice.value || 0,
        notes: patientForm.value.notes.trim(),
      };
      lastRes = await axios.post(`/api/v1/scheduling/public/${slugOrId.value}/book`, payload);
    }

    successData.value = lastRes?.data || { success: true };
    selectedSlotsList.value = [];
    showBookingModal.value = false;
  } catch (err) {
    alert(err.response?.data?.error || 'Erro ao realizar agendamento.');
  } finally {
    isBooking.value = false;
  }
};

const resetBooking = () => {
  successData.value = null;
  selectedSlotInfo.value = null;
  showBookingModal.value = false;
  isTrialClass.value = false;
  patientForm.value = { name: '', phone: '', email: '', notes: '' };
  if (viewMode.value === 'agenda') {
    fetchWeeklySlots();
  } else {
    fetchFixedGridData();
  }
};

const formatPrice = val => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(val) || 0);
};

const formatDateFriendly = dateStr => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(d);
};

watch(viewMode, () => {
  selectedSlotInfo.value = null;
  if (viewMode.value === 'agenda') {
    fetchWeeklySlots();
  } else {
    fetchFixedGridData();
  }
});

watch([selectedService, selectedProfessional], () => {
  selectedSlotInfo.value = null;
  if (viewMode.value === 'agenda') {
    fetchWeeklySlots();
  } else {
    fetchFixedGridData();
  }
});

onMounted(async () => {
  await fetchPublicConfig();
  fetchWeeklySlots();
  fetchFixedGridData();
});
</script>

<template>
  <div class="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-center items-center p-3 sm:p-6 font-sans select-none">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex flex-col items-center gap-3">
      <div class="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent"></div>
      <span class="text-xs text-slate-400">Carregando horários de atendimento...</span>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="max-w-md w-full p-8 rounded-3xl bg-slate-900 border border-slate-800 text-center space-y-4 shadow-2xl"
    >
      <div class="w-12 h-12 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center mx-auto text-xl font-bold">
        ✕
      </div>
      <h2 class="text-base font-bold text-white">Página Indisponível</h2>
      <p class="text-xs text-slate-400 leading-relaxed">{{ error }}</p>
    </div>

    <!-- Success Confirmation View -->
    <div
      v-else-if="successData"
      class="max-w-lg w-full p-8 rounded-3xl bg-slate-900 border border-slate-800 text-center space-y-6 shadow-2xl animate-fade-in"
    >
      <div class="w-14 h-14 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto text-2xl font-bold">
        ✓
      </div>

      <div>
        <h2 class="text-lg font-bold text-white">Agendamento Confirmado!</h2>
        <p class="text-xs text-slate-400 mt-1">
          Recebemos o seu agendamento na <strong>{{ company.name }}</strong>.
        </p>
      </div>

      <!-- Summary Card -->
      <div class="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 text-left text-xs space-y-2.5">
        <div class="flex items-center justify-between">
          <span class="text-slate-400">Serviço:</span>
          <span class="font-bold text-white">{{ successData.appointment?.service?.name }}</span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-slate-400">Profissional:</span>
          <span class="font-bold text-slate-300">{{ successData.appointment?.professional?.name || 'Profissional da Clínica' }}</span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-slate-400">Data e Horário:</span>
          <span class="font-bold text-blue-400">
            {{ new Date(successData.appointment?.start_time).toLocaleString('pt-BR') }}
          </span>
        </div>
        <div class="flex items-center justify-between pt-2 border-t border-slate-800 text-slate-300">
          <span>Paciente:</span>
          <span class="font-medium">{{ successData.appointment?.patient?.name }}</span>
        </div>
      </div>

      <button
        type="button"
        class="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl shadow-md transition-all cursor-pointer"
        @click="resetBooking"
      >
        Agendar Outro Atendimento
      </button>
    </div>

    <!-- MAIN BOOKING CONTAINER: FULL WIDTH HEADER + FILTERS TOOLBAR + WIDE GRID MATRIX -->
    <div
      v-else
      class="max-w-7xl w-full bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col"
    >
      <!-- 1. CABEÇALHO PRINCIPAL COM LOGO E ABAS DE NAVEGAÇÃO (AGENDA | GRADE DE VAGAS) -->
      <div class="px-6 py-4 border-b border-slate-800 bg-slate-950/80 flex flex-wrap items-center justify-between gap-4">
        <!-- Empresa / Identidade -->
        <div class="flex items-center gap-3.5">
          <div class="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-bold flex items-center justify-center text-lg shadow-md border border-blue-500/30">
            {{ company.name?.charAt(0).toUpperCase() || 'A' }}
          </div>
          <div>
            <div class="flex items-center gap-2.5">
              <h1 class="text-base font-bold text-white leading-tight">
                {{ company.name }}
              </h1>
              <span class="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                Link de Atendimento
              </span>
            </div>
            <p class="text-xs text-slate-400 mt-0.5">Consulta de disponibilidade e agendamento de vagas</p>
          </div>
        </div>

        <!-- Abas de Navegação Principal: [ 📅 Agenda ] [ 🗂️ Grade de Vagas ] -->
        <div class="flex items-center p-1 bg-slate-950 border border-slate-800 rounded-2xl shadow-inner">
          <button
            type="button"
            class="h-9 px-4 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2"
            :class="viewMode === 'agenda' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-white'"
            @click="viewMode = 'agenda'"
          >
            <span>📅</span>
            <span>Agenda</span>
          </button>
          <button
            type="button"
            class="h-9 px-4 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2"
            :class="viewMode === 'grid' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-white'"
            @click="viewMode = 'grid'"
          >
            <span>🗂️</span>
            <span>Grade de Vagas</span>
          </button>
        </div>
      </div>

      <!-- 2. BARRA DE FILTROS RÁPIDOS & CONTROLES (HORIZONTAL PERFEITAMENTE ALINHADA) -->
      <div class="px-6 py-3.5 border-b border-slate-800/80 bg-slate-900/90 flex flex-wrap items-center justify-between gap-4">
        <!-- Filtros de Serviço e Profissional (Lado Esquerdo) -->
        <div class="flex flex-wrap items-center gap-3">
          <!-- Dropdown de Serviço (Card Único com Seta Única) -->
          <div class="relative flex items-center h-10 bg-slate-950 border border-slate-800 hover:border-slate-700 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/50 rounded-xl px-3.5 pr-8 text-xs shadow-xs transition-all cursor-pointer">
            <span class="text-sm mr-2.5 select-none">🧘</span>
            <div class="flex flex-col justify-center select-none">
              <span class="text-[9px] font-bold text-slate-500 uppercase tracking-wider leading-none">Serviço</span>
              <span class="text-xs font-bold text-white leading-tight truncate max-w-[220px]">
                {{ selectedService?.name || 'Selecione' }} ({{ formatPrice(selectedService?.price) }}){{ selectedService?.allow_trial_class ? ' ⭐' : '' }}
              </span>
            </div>
            <span class="absolute right-3 text-[10px] text-slate-400 select-none pointer-events-none">▼</span>
            <!-- Select Invisível por cima para acionar o dropdown nativo perfeitamente -->
            <select
              v-model="selectedService"
              class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            >
              <option v-for="s in services" :key="s.id" :value="s" class="bg-slate-900 text-white">
                {{ s.name }} ({{ formatPrice(s.price) }}){{ s.allow_trial_class ? ' ⭐' : '' }}
              </option>
            </select>
          </div>

          <!-- Dropdown de Profissional (Card Único com Seta Única) -->
          <div class="relative flex items-center h-10 bg-slate-950 border border-slate-800 hover:border-slate-700 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/50 rounded-xl px-3.5 pr-8 text-xs shadow-xs transition-all cursor-pointer">
            <span class="text-sm mr-2.5 select-none">👩‍⚕️</span>
            <div class="flex flex-col justify-center select-none">
              <span class="text-[9px] font-bold text-slate-500 uppercase tracking-wider leading-none">Profissional</span>
              <span class="text-xs font-bold text-white leading-tight truncate max-w-[220px]">
                {{ selectedProfessional ? `${selectedProfessional.name} (${selectedProfessional.specialty || 'Geral'})` : 'Todos os Profissionais' }}
              </span>
            </div>
            <span class="absolute right-3 text-[10px] text-slate-400 select-none pointer-events-none">▼</span>
            <!-- Select Invisível por cima para acionar o dropdown nativo perfeitamente -->
            <select
              v-model="selectedProfessional"
              class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            >
              <option :value="null" class="bg-slate-900 text-white">Todos os Profissionais</option>
              <option v-for="prof in professionals" :key="prof.id" :value="prof" class="bg-slate-900 text-white">
                {{ prof.name }} ({{ prof.specialty || 'Geral' }})
              </option>
            </select>
          </div>
        </div>

        <!-- Controles de Semana (em Agenda), Turnos e Botão de Copiar p/ WhatsApp (Lado Direito) -->
        <div class="flex flex-wrap items-center gap-3">
          <!-- Botão Inteligente: Copiar Vagas para WhatsApp (Super Produtividade da Atendente) -->
          <button
            type="button"
            class="h-10 px-3.5 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2 border shadow-xs"
            :class="hasCopiedWhatsApp ? 'bg-emerald-600 border-emerald-400 text-white animate-bounce' : 'bg-slate-950 border-slate-800 text-slate-300 hover:text-white hover:bg-slate-800 hover:border-slate-700'"
            title="Copiar lista de horários livres para colar no WhatsApp do cliente"
            @click="copySlotsForWhatsApp"
          >
            <span>{{ hasCopiedWhatsApp ? '✓' : '📋' }}</span>
            <span class="hidden sm:inline">{{ hasCopiedWhatsApp ? 'Copiado para o WhatsApp!' : 'Copiar Vagas p/ WhatsApp' }}</span>
            <span class="sm:hidden">{{ hasCopiedWhatsApp ? 'Copiado!' : 'Copiar' }}</span>
          </button>

          <!-- Navegador de Semanas (Apenas no Modo Agenda) -->
          <div v-if="viewMode === 'agenda'" class="flex items-center gap-1.5 h-10 bg-slate-950 border border-slate-800 px-2 rounded-xl text-xs">
            <button
              type="button"
              class="w-7 h-7 rounded-lg bg-slate-900 border border-slate-800 hover:border-slate-700 text-slate-300 hover:text-white flex items-center justify-center text-xs font-bold transition-all cursor-pointer"
              title="Semana anterior"
              @click="prevWeek"
            >
              ‹
            </button>
            <button
              type="button"
              class="h-7 px-2.5 text-[11px] font-bold rounded-lg bg-slate-900 border border-slate-800 hover:border-slate-700 text-blue-400 hover:text-blue-300 transition-all cursor-pointer flex items-center"
              @click="goToToday"
            >
              Hoje
            </button>
            <button
              type="button"
              class="w-7 h-7 rounded-lg bg-slate-900 border border-slate-800 hover:border-slate-700 text-slate-300 hover:text-white flex items-center justify-center text-xs font-bold transition-all cursor-pointer"
              title="Próxima semana"
              @click="nextWeek"
            >
              ›
            </button>
            <span class="text-xs font-bold text-white px-2 font-mono">
              {{ agendaWeekRangeLabel }}
            </span>
          </div>

          <!-- Filtro de Turnos -->
          <div class="flex items-center gap-1 h-10 p-1 bg-slate-950 border border-slate-800 rounded-xl">
            <button
              type="button"
              class="h-8 px-3 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5"
              :class="activePeriod === 'morning' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'"
              @click="activePeriod = 'morning'"
            >
              <span>🌅</span>
              <span>Manhã</span>
            </button>
            <button
              type="button"
              class="h-8 px-3 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5"
              :class="activePeriod === 'afternoon' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'"
              @click="activePeriod = 'afternoon'"
            >
              <span>☀️</span>
              <span>Tarde</span>
            </button>
            <button
              type="button"
              class="h-8 px-3 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5"
              :class="activePeriod === 'night' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'"
              @click="activePeriod = 'night'"
            >
              <span>🌙</span>
              <span>Noite</span>
            </button>
            <button
              type="button"
              class="h-8 px-3 text-xs font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1.5"
              :class="activePeriod === 'all' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'"
              @click="activePeriod = 'all'"
            >
              <span>📋</span>
              <span>Dia Todo</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 3. ÁREA DA GRADE SEMANAL AMPLA (FULL WIDTH) -->
      <div class="p-5 sm:p-6 flex-1 overflow-x-auto bg-slate-900/40">
        <!-- Loading Spinners -->
        <div v-if="isLoadingWeeklySlots || isLoadingFixedGrid" class="flex flex-col items-center justify-center py-16 gap-2">
          <div class="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
          <span class="text-xs text-slate-400">Verificando vagas disponíveis...</span>
        </div>

        <!-- VIEW 1: AGENDA (VISÃO SEMANAL POR DATAS ESPECÍFICAS) -->
        <div v-else-if="viewMode === 'agenda'" class="space-y-3">
          <div class="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900 shadow-sm">
            <div class="min-w-[950px]">
              <!-- Days Header -->
              <div class="grid grid-cols-[85px_repeat(7,minmax(0,1fr))] border-b border-slate-800 bg-slate-900/95 sticky top-0 z-20">
                <div class="p-2.5 text-xs font-bold uppercase tracking-wider text-slate-300 text-center border-r border-slate-800 bg-slate-950/60 flex items-center justify-center">
                  HORÁRIO
                </div>
                <div
                  v-for="d in agendaWeekDaysList"
                  :key="d.dateStr"
                  class="p-1.5 text-center border-r border-slate-800 last:border-r-0 flex items-center justify-center"
                >
                  <div
                    class="w-full py-2 px-2 rounded-xl transition-all flex flex-col items-center justify-center select-none"
                    :class="[
                      d.isToday
                        ? 'bg-blue-600/20 border border-blue-500 shadow-md shadow-blue-500/20'
                        : 'bg-slate-950/40 hover:bg-slate-800/80 border border-slate-800/60'
                    ]"
                  >
                    <span
                      class="block text-[11px] font-extrabold uppercase tracking-wider leading-none"
                      :class="d.isToday ? 'text-blue-400 font-extrabold' : 'text-slate-400'"
                    >
                      {{ d.label }}
                    </span>
                    <span
                      class="block text-sm font-extrabold font-mono mt-1 leading-none tracking-tight"
                      :class="d.isToday ? 'text-white' : 'text-slate-100'"
                    >
                      {{ d.formattedDay }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Time Slots Rows -->
              <div class="divide-y divide-slate-800/80 bg-slate-900">
                <div
                  v-for="hour in displayedHours"
                  :key="hour"
                  class="grid grid-cols-[85px_repeat(7,minmax(0,1fr))] min-h-[85px] hover:bg-slate-850/40 transition-colors"
                >
                  <!-- Slot Time Label com Pílula Destacada -->
                  <div class="p-2 text-center border-r border-slate-800 flex items-start justify-center pt-2.5 bg-slate-950/20 select-none">
                    <span class="inline-flex items-center justify-center px-2 py-1 rounded-md bg-slate-900 border border-slate-700/80 text-xs font-extrabold text-slate-100 font-mono shadow-xs">
                      {{ hour }}
                    </span>
                  </div>

                  <!-- Day Columns -->
                  <div
                    v-for="d in agendaWeekDaysList"
                    :key="`${d.dateStr}-${hour}`"
                    class="p-1.5 border-r border-slate-800 last:border-r-0 relative group transition-colors flex items-center justify-center"
                    :class="[d.isToday ? 'bg-blue-950/10' : '']"
                  >
                    <!-- Botão Clean de Vaga Disponível -->
                    <button
                      v-if="getSlotForAgendaCell(d.dateStr, hour)"
                      type="button"
                      class="group w-full py-2.5 px-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex flex-col items-center justify-center border shadow-xs"
                      :class="[
                        selectedSlotInfo?.mode === 'agenda' && selectedSlotInfo?.dateStr === d.dateStr && selectedSlotInfo?.timeSlot === hour
                          ? 'bg-blue-600 border-blue-400 text-white ring-2 ring-blue-500 shadow-md scale-[1.02]'
                          : getSlotForAgendaCell(d.dateStr, hour).total_spots <= 2
                            ? 'bg-amber-950/25 border-amber-500/40 text-amber-300 hover:border-amber-400 hover:bg-amber-950/40'
                            : 'bg-slate-900/90 border-slate-800/80 text-slate-200 hover:border-blue-500/50 hover:bg-slate-800'
                      ]"
                      @click="selectAgendaSlot(d.dateStr, getSlotForAgendaCell(d.dateStr, hour))"
                    >
                      <span class="font-bold group-hover:hidden">
                        {{ getSlotForAgendaCell(d.dateStr, hour).total_spots <= 2 ? `⚠️ ${getSlotForAgendaCell(d.dateStr, hour).total_spots} vaga${getSlotForAgendaCell(d.dateStr, hour).total_spots > 1 ? 's' : ''}` : `${getSlotForAgendaCell(d.dateStr, hour).total_spots} vagas` }}
                      </span>
                      <span class="hidden group-hover:inline-block text-white font-bold">
                        + Agendar
                      </span>
                      <span
                        class="text-[10px] font-normal"
                        :class="getSlotForAgendaCell(d.dateStr, hour).total_spots <= 2 ? 'text-amber-400/90 font-semibold' : 'text-slate-400'"
                      >
                        {{ getSlotForAgendaCell(d.dateStr, hour).total_spots <= 2 ? 'Últimas vagas' : 'Disponível' }}
                      </span>
                    </button>

                    <!-- Horário Vazio / Indisponível -->
                    <span v-else class="text-slate-700 text-xs select-none">—</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- VIEW 2: GRADE DE VAGAS (QUADRO FIXO PERPÉTUO POR DIAS DA SEMANA) -->
        <div v-else-if="viewMode === 'grid'" class="space-y-3">
          <div class="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900 shadow-sm">
            <div class="min-w-[950px]">
              <!-- Days Header -->
              <div class="grid grid-cols-[85px_repeat(6,minmax(0,1fr))] border-b border-slate-800 bg-slate-950/90 sticky top-0 z-20">
                <div class="p-2.5 text-xs font-bold uppercase tracking-wider text-slate-300 text-center border-r border-slate-800 bg-slate-950/60 flex items-center justify-center">
                  HORÁRIO
                </div>
                <div
                  v-for="d in fixedDaysOfWeek"
                  :key="d.key"
                  class="p-1.5 text-center border-r border-slate-800 last:border-r-0 flex items-center justify-center"
                >
                  <div
                    class="w-full py-2 px-2 rounded-xl transition-all flex flex-col items-center justify-center select-none"
                    :class="[
                      d.key === todayDayKey
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30 border border-blue-400/80 ring-2 ring-blue-500/20'
                        : 'bg-slate-950/50 hover:bg-slate-800/80 border border-slate-800/60'
                    ]"
                  >
                    <span class="block text-xs font-black uppercase tracking-wider leading-none text-white">
                      {{ d.label }}
                    </span>
                    <span
                      class="block text-[10px] font-medium mt-1 leading-none tracking-tight"
                      :class="d.key === todayDayKey ? 'text-blue-100' : 'text-slate-400'"
                    >
                      {{ d.full }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Grid Slots Rows -->
              <div class="divide-y divide-slate-800/80 bg-slate-900">
                <div
                  v-for="hour in displayedHours"
                  :key="hour"
                  class="grid grid-cols-[85px_repeat(6,minmax(0,1fr))] min-h-[75px] hover:bg-slate-850/30 transition-colors"
                >
                  <!-- Slot Time Label com Pílula Destacada -->
                  <div class="p-2 text-center border-r border-slate-800 flex items-start justify-center pt-2.5 bg-slate-950/20 select-none">
                    <span class="inline-flex items-center justify-center px-2 py-1 rounded-md bg-slate-900 border border-slate-700/80 text-xs font-extrabold text-slate-100 font-mono shadow-xs">
                      {{ hour }}
                    </span>
                  </div>

                  <!-- Day Columns -->
                  <div
                    v-for="d in fixedDaysOfWeek"
                    :key="`${d.key}-${hour}`"
                    class="p-1.5 border-r border-slate-800 last:border-r-0 relative group transition-colors flex items-center justify-center"
                  >
                    <!-- Botão Clean de Vaga Disponível -->
                    <button
                      v-if="getSlotForFixedGridCell(d.key, hour)?.is_available"
                      type="button"
                      class="group w-full py-2.5 px-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex flex-col items-center justify-center border shadow-xs"
                      :class="[
                        isFixedSlotSelected(d.key, hour)
                          ? 'bg-blue-600 border-blue-400 text-white ring-2 ring-blue-500 shadow-md scale-[1.02]'
                          : getSlotForFixedGridCell(d.key, hour).free_spots <= 2
                            ? 'bg-amber-950/25 border-amber-500/40 text-amber-300 hover:border-amber-400 hover:bg-amber-950/40'
                            : 'bg-slate-900/90 border-slate-800/80 text-slate-200 hover:border-blue-500/50 hover:bg-slate-800'
                      ]"
                      @click="selectFixedGridSlot(d.key, hour, getSlotForFixedGridCell(d.key, hour))"
                    >
                      <span class="font-bold group-hover:hidden">
                        {{ getSlotForFixedGridCell(d.key, hour).free_spots <= 2 ? `⚠️ ${getSlotForFixedGridCell(d.key, hour).free_spots} vaga${getSlotForFixedGridCell(d.key, hour).free_spots > 1 ? 's' : ''}` : `${getSlotForFixedGridCell(d.key, hour).free_spots} vagas` }}
                      </span>
                      <span class="hidden group-hover:inline-block text-white font-bold">
                        {{ isFixedSlotSelected(d.key, hour) ? '✕ Remover' : '+ Selecionar' }}
                      </span>
                      <span
                        class="text-[10px] font-normal"
                        :class="getSlotForFixedGridCell(d.key, hour).free_spots <= 2 ? 'text-amber-400/90 font-semibold' : 'text-slate-400'"
                      >
                        {{ getSlotForFixedGridCell(d.key, hour).free_spots <= 2 ? 'Últimas vagas' : 'Disponível' }}
                      </span>
                    </button>

                    <!-- Horário Lotado -->
                    <div
                      v-else-if="getSlotForFixedGridCell(d.key, hour) && !getSlotForFixedGridCell(d.key, hour).is_available"
                      class="w-full py-2 px-1.5 rounded-xl text-xs font-semibold bg-rose-950/20 border border-rose-900/30 text-rose-400/70 flex flex-col items-center justify-center select-none"
                    >
                      <span class="line-through">Lotado</span>
                      <span class="text-[10px] text-slate-500">0 vagas</span>
                    </div>

                    <!-- Horário Vazio / Fora de Escala -->
                    <span v-else class="text-slate-700 text-xs select-none">—</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- BARRA FLUTUANTE DE MULTI-SELEÇÃO NA GRADE FIXA (2x ou 3x na semana) -->
    <div
      v-if="viewMode === 'grid' && selectedSlotsList.length > 0"
      class="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900/95 border border-blue-500/50 px-6 py-3.5 rounded-2xl shadow-2xl backdrop-blur-md flex flex-col sm:flex-row items-center gap-4 text-xs animate-fade-in"
    >
      <div class="flex items-center gap-2.5 text-white">
        <span class="w-6 h-6 rounded-full bg-blue-600 text-white font-black flex items-center justify-center text-xs shrink-0 shadow-xs">
          {{ selectedSlotsList.length }}
        </span>
        <span class="font-bold">
          {{ selectedSlotsList.length === 1 ? '1 horário selecionado:' : `${selectedSlotsList.length} horários selecionados (${selectedSlotsList.length}x na semana):` }}
        </span>
        <span class="text-blue-300 font-mono font-semibold">
          {{ selectedSlotsList.map(s => `${s.dayLabel.split('-')[0]} às ${s.timeSlot}`).join(' • ') }}
        </span>
      </div>

      <div class="flex items-center gap-2">
        <button
          type="button"
          class="px-3.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-semibold cursor-pointer transition-all border border-slate-700"
          @click="selectedSlotsList = []"
        >
          Limpar
        </button>
        <button
          type="button"
          class="px-5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold cursor-pointer transition-all shadow-md flex items-center gap-1.5"
          @click="openBookingModalWithSelection"
        >
          <span>Continuar Matrícula ➔</span>
        </button>
      </div>
    </div>

    <!-- MODAL DE AGENDAMENTO / MATRÍCULA (INSPIRADO NO MODAL TRADICIONAL) -->
    <div
      v-if="showBookingModal && selectedSlotInfo"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in"
      @click.self="closeBookingModal"
    >
      <div class="w-full max-w-xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden text-slate-100 flex flex-col max-h-[92vh]">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
          <div class="flex items-center gap-3">
            <div
              class="w-8 h-8 rounded-xl flex items-center justify-center text-sm shadow-xs"
              :class="isTrialClass ? 'bg-amber-500/20 border border-amber-500/30 text-amber-400' : 'bg-emerald-600/20 border border-emerald-500/30 text-emerald-400'"
            >
              {{ isTrialClass ? '⭐' : '📅' }}
            </div>
            <div>
              <h3 class="text-sm font-bold text-white flex items-center gap-2">
                <span>{{ isTrialClass ? 'Agendar Aula Experimental' : (selectedSlotInfo.mode === 'grid' ? 'Nova Matrícula na Grade Fixa' : 'Novo Agendamento') }}</span>
              </h3>
              <p class="text-xs text-slate-400">
                {{ isTrialClass ? 'Agende uma aula experimental avulsa/teste de 1 sessão.' : (selectedSlotInfo.mode === 'grid' ? 'Reserve horários fixos semanais para o aluno/paciente.' : 'Reserve seu atendimento na data e horário selecionados.') }}
              </p>
            </div>
          </div>
          <button type="button" class="text-slate-400 hover:text-white cursor-pointer font-bold text-base" @click="closeBookingModal">✕</button>
        </div>

        <!-- Body -->
        <div class="p-6 overflow-y-auto space-y-4 flex-1">
          <!-- Nome do Paciente -->
          <div>
            <label class="block text-xs font-semibold text-slate-300 mb-1">
              Nome do Paciente <span class="text-red-400">*</span>
            </label>
            <input
              v-model="patientForm.name"
              type="text"
              placeholder="Digite o nome do paciente..."
              class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-white"
            />
          </div>

          <!-- WhatsApp / Celular e E-mail -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">
                WhatsApp / Telefone <span class="text-red-400">*</span>
              </label>
              <input
                v-model="patientForm.phone"
                type="text"
                placeholder="(11) 99999-9999"
                class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-white"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">
                E-mail (Opcional)
              </label>
              <input
                v-model="patientForm.email"
                type="email"
                placeholder="paciente@email.com"
                class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-white"
              />
            </div>
          </div>

          <!-- Serviço e Profissional -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">
                Serviço <span class="text-red-400">*</span>
              </label>
              <select
                v-model="selectedService"
                class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl focus:ring-1 focus:ring-blue-500 focus:outline-none text-white cursor-pointer"
              >
                <option v-for="s in services" :key="s.id" :value="s">
                  {{ s.name }} ({{ s.attendance_type === 'group' ? '👥 Grupo' : '👤 Particular' }} - {{ s.duration_minutes }} min)
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">
                Profissional <span class="text-red-400">*</span>
              </label>
              <select
                v-model="selectedProfessional"
                class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl focus:ring-1 focus:ring-blue-500 focus:outline-none text-white cursor-pointer"
              >
                <option :value="null">Qualquer profissional disponível</option>
                <option v-for="prof in professionals" :key="prof.id" :value="prof">
                  {{ prof.name }} ({{ prof.specialty || 'Geral' }})
                </option>
              </select>
            </div>
          </div>

          <!-- Checkbox de Aula Experimental (Estilo Card) -->
          <div
            v-if="selectedService?.allow_trial_class"
            class="p-3 bg-slate-950/60 border border-slate-700 rounded-xl flex items-center justify-between animate-fade-in"
          >
            <label class="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                v-model="isTrialClass"
                type="checkbox"
                class="w-4 h-4 rounded text-amber-500 bg-slate-900 border-slate-700 focus:ring-amber-400 cursor-pointer"
              />
              <div>
                <span class="text-xs font-bold text-amber-300 flex items-center gap-1">
                  <span>⭐</span> Aula Experimental
                </span>
                <span class="text-[10px] text-slate-400 block">
                  Trava a matrícula como atendimento avulso/teste de 1 sessão
                </span>
              </div>
            </label>
            <span v-if="isTrialClass" class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
              Atendimento Único
            </span>
          </div>

          <!-- Modalidade do Agendamento / Matrícula -->
          <div v-if="!isTrialClass">
            <label class="block text-xs font-semibold text-slate-300 mb-1.5">
              Modalidade {{ selectedSlotInfo.mode === 'grid' ? 'da Matrícula' : 'do Atendimento' }} <span class="text-red-400">*</span>
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                type="button"
                class="py-2.5 px-3 rounded-xl border text-left text-xs font-bold transition-all flex flex-col gap-0.5 cursor-pointer"
                :class="
                  bookingBillingType === 'recurring'
                    ? 'bg-amber-600/20 border-amber-500 text-amber-300 ring-1 ring-amber-500 shadow-xs'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
                "
                @click="setBillingType('recurring')"
              >
                <div class="flex items-center gap-1.5 font-bold text-xs">
                  <span>🧘‍♀️</span>
                  <span>Mensalidade</span>
                </div>
                <span class="text-[10px] font-normal text-slate-400">Vaga fixa perpétua na grade</span>
              </button>

              <button
                type="button"
                class="py-2.5 px-3 rounded-xl border text-left text-xs font-bold transition-all flex flex-col gap-0.5 cursor-pointer"
                :class="
                  bookingBillingType === 'package'
                    ? 'bg-cyan-600/20 border-cyan-500 text-cyan-300 ring-1 ring-cyan-500 shadow-xs'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
                "
                @click="setBillingType('package')"
              >
                <div class="flex items-center gap-1.5 font-bold text-xs">
                  <span>🩺</span>
                  <span>Pacote de Sessões</span>
                </div>
                <span class="text-[10px] font-normal text-slate-400">Total fixo (ex: 10 sessões)</span>
              </button>

              <button
                type="button"
                class="py-2.5 px-3 rounded-xl border text-left text-xs font-bold transition-all flex flex-col gap-0.5 cursor-pointer"
                :class="
                  bookingBillingType === 'single'
                    ? 'bg-blue-600/20 border-blue-500 text-blue-300 ring-1 ring-blue-500 shadow-xs'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
                "
                @click="setBillingType('single')"
              >
                <div class="flex items-center gap-1.5 font-bold text-xs">
                  <span>⚡</span>
                  <span>Sessão Avulsa</span>
                </div>
                <span class="text-[10px] font-normal text-slate-400">1 atendimento pontual</span>
              </button>
            </div>
          </div>

          <!-- Pílulas Clássicas de Dias da Semana (Modo Grade Fixa) -->
          <!-- Pílulas Clássicas de Dias da Semana (Modo Grade Fixa - Oculto em Sessão Avulsa e Aula Experimental) -->
          <div v-if="selectedSlotInfo.mode === 'grid' && !isTrialClass && bookingBillingType !== 'single'">
            <div class="flex items-center justify-between mb-1.5">
              <label class="block text-xs font-semibold text-slate-300">
                Dias Fixos da Semana <span class="text-red-400">*</span>
              </label>
              <span class="text-[10px] text-blue-300 font-semibold">
                {{ modalSelectedDays.length }} dia{{ modalSelectedDays.length > 1 ? 's' : '' }} selecionado{{ modalSelectedDays.length > 1 ? 's' : '' }}
              </span>
            </div>

            <div class="grid grid-cols-6 gap-1.5">
              <button
                v-for="d in fixedDaysOfWeek"
                :key="d.key"
                type="button"
                class="py-2 text-xs font-bold rounded-xl border transition-all text-center cursor-pointer select-none"
                :class="[
                  modalSelectedDays.includes(d.key)
                    ? (bookingBillingType === 'recurring' ? 'bg-amber-600 border-amber-500 text-white shadow-xs' : 'bg-blue-600 border-blue-500 text-white shadow-xs')
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-200'
                ]"
                @click="toggleModalDay(d.key)"
              >
                {{ d.label }}
              </button>
            </div>
          </div>

          <!-- Card de Horário Fixo com Turnos e Pílulas de Horário -->
          <div class="p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800 space-y-2.5">
            <div class="flex items-center justify-between">
              <label class="block text-xs font-semibold text-slate-300">
                {{ isTrialClass ? 'Horário da Aula Experimental' : 'Horário Fixo' }} <span class="text-red-400">*</span>
              </label>
              <div class="flex items-center gap-1 p-0.5 bg-slate-900 border border-slate-800 rounded-lg">
                <button
                  type="button"
                  class="px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                  :class="activeModalShift === 'morning' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'"
                  @click="activeModalShift = 'morning'"
                >
                  🌅 Manhã
                </button>
                <button
                  type="button"
                  class="px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                  :class="activeModalShift === 'afternoon' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'"
                  @click="activeModalShift = 'afternoon'"
                >
                  ☀️ Tarde
                </button>
                <button
                  type="button"
                  class="px-2 py-0.5 text-[10px] font-bold rounded-md transition-all cursor-pointer"
                  :class="activeModalShift === 'night' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-slate-200'"
                  @click="activeModalShift = 'night'"
                >
                  🌙 Noite
                </button>
              </div>
            </div>

            <div class="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
              <button
                v-for="t in modalShiftTimes"
                :key="t"
                type="button"
                class="py-1.5 px-2 text-xs font-mono font-bold rounded-xl border transition-all text-center cursor-pointer select-none"
                :class="[
                  bookingTimeSlot === t
                    ? 'bg-blue-600 border-blue-500 text-white shadow-md'
                    : 'bg-slate-900 border-slate-800 hover:border-slate-700 text-slate-300'
                ]"
                @click="bookingTimeSlot = t"
              >
                {{ t }}
              </button>
            </div>
          </div>

          <!-- Configuração Específica por Modalidade -->
          <!-- 1. SESSÃO AVULSA OU AULA EXPERIMENTAL -->
          <div
            v-if="isTrialClass || bookingBillingType === 'single'"
            class="p-3 bg-slate-950/80 border border-slate-800 rounded-2xl animate-fade-in space-y-1"
          >
            <label class="block text-xs font-semibold text-slate-300 mb-1">
              Data do Atendimento <span class="text-red-400">*</span>
            </label>
            <input
              v-model="bookingStartDate"
              type="date"
              class="w-full h-9 px-3 text-xs bg-slate-900 border border-slate-700 rounded-xl text-white cursor-pointer font-mono"
            />
          </div>

          <!-- 2. PACOTE DE SESSÕES -->
          <div
            v-else-if="bookingBillingType === 'package'"
            class="grid grid-cols-2 gap-3 p-3 bg-slate-950/80 border border-cyan-500/30 rounded-2xl animate-fade-in"
          >
            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">
                Total de Sessões do Pacote <span class="text-red-400">*</span>
              </label>
              <input
                v-model.number="bookingTotalSessions"
                type="number"
                min="1"
                max="100"
                class="w-full h-9 px-3 text-xs bg-slate-900 border border-slate-700 rounded-xl text-white font-bold font-mono"
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">
                Data de Início
              </label>
              <input
                v-model="bookingStartDate"
                type="date"
                class="w-full h-9 px-3 text-xs bg-slate-900 border border-slate-700 rounded-xl text-white cursor-pointer font-mono"
              />
            </div>
          </div>

          <!-- 3. MENSALIDADE RECORRENTE -->
          <div
            v-else-if="bookingBillingType === 'recurring'"
            class="p-4 bg-slate-950/90 rounded-2xl border border-amber-500/30 space-y-3 animate-fade-in"
          >
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-amber-300 flex items-center gap-1.5">
                <span>🧘‍♀️</span> Matrícula Recorrente & Vaga Fixa
              </span>
              <span class="text-[11px] font-semibold text-slate-400">
                Renovação mensal contínua
              </span>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[11px] font-semibold text-slate-300 mb-1">
                  Dia de Vencimento da Mensalidade
                </label>
                <div class="flex items-center gap-1">
                  <button
                    v-for="d in [5, 10, 15, 20]"
                    :key="d"
                    type="button"
                    class="px-2 py-1 text-xs font-bold font-mono rounded-lg border transition-all cursor-pointer"
                    :class="bookingDueDay === d ? 'bg-amber-600 border-amber-500 text-white shadow-xs' : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-200'"
                    @click="bookingDueDay = d"
                  >
                    {{ String(d).padStart(2, '0') }}
                  </button>
                  <input
                    v-model.number="bookingDueDay"
                    type="number"
                    min="1"
                    max="31"
                    class="w-12 h-7 px-1.5 text-xs bg-slate-900 border border-slate-700 rounded-lg text-white font-mono text-center"
                  />
                </div>
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-slate-300 mb-1">
                  Data de Início das Aulas
                </label>
                <input
                  v-model="bookingStartDate"
                  type="date"
                  class="w-full h-8 px-2.5 text-xs bg-slate-900 border border-slate-700 rounded-lg text-white cursor-pointer font-mono"
                />
              </div>
            </div>
          </div>

          <!-- Valor e Observações -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">
                Valor Mensal / Pacote (R$)
              </label>
              <input
                v-model.number="bookingPrice"
                type="number"
                step="0.01"
                placeholder="0.00"
                class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl text-white font-mono"
              />
            </div>

            <div>
              <label class="block text-xs font-semibold text-slate-300 mb-1">
                Observações Internas
              </label>
              <input
                v-model="patientForm.notes"
                type="text"
                placeholder="Ex: Aluno novo, foco em postura.."
                class="w-full h-9 px-3 text-xs bg-slate-800 border border-slate-700 rounded-xl focus:ring-1 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-white"
              />
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-slate-800 flex items-center justify-between bg-slate-950/60">
          <button
            type="button"
            class="text-xs font-semibold text-slate-400 hover:text-white px-4 py-2 rounded-xl hover:bg-slate-800 transition-colors cursor-pointer"
            @click="closeBookingModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            :disabled="!patientForm.name.trim() || !patientForm.phone.trim() || isBooking"
            class="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl shadow-lg transition-all cursor-pointer flex items-center justify-center gap-2"
            @click="confirmBooking"
          >
            <div v-if="isBooking" class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            <span>{{ isBooking ? 'Confirmando...' : (selectedSlotInfo.mode === 'grid' ? 'Confirmar Matrícula na Grade' : 'Confirmar Agendamento') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
